/**
 * HowItWorksSection — the six-stage stepper (Target → Diagnose → Build →
 * Prove → Position → Defend). Anchor target for NewUserHero's
 * "See how it works" CTA (id="how").
 */

import { Box, Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

interface Stage {
  num: number;
  titleKey: string;
  bodyKey: string;
}

const STAGES: Stage[] = [
  { num: 1, titleKey: "HowItWorksSection.stage1Title", bodyKey: "HowItWorksSection.stage1Body" },
  { num: 2, titleKey: "HowItWorksSection.stage2Title", bodyKey: "HowItWorksSection.stage2Body" },
  { num: 3, titleKey: "HowItWorksSection.stage3Title", bodyKey: "HowItWorksSection.stage3Body" },
  { num: 4, titleKey: "HowItWorksSection.stage4Title", bodyKey: "HowItWorksSection.stage4Body" },
  { num: 5, titleKey: "HowItWorksSection.stage5Title", bodyKey: "HowItWorksSection.stage5Body" },
  { num: 6, titleKey: "HowItWorksSection.stage6Title", bodyKey: "HowItWorksSection.stage6Body" },
];

export default function HowItWorksSection() {
  const { t } = useTranslation("home");
  return (
    <Box id="how" component="section" sx={{ scrollMarginTop: "80px" }}>
      <Typography
        component="span"
        sx={{ fontSize: 14, fontWeight: 500, color: "secondary.main", display: "block", mb: 1 }}
      >
        {t("HowItWorksSection.eyebrow")}
      </Typography>
      <Typography component="h2" sx={{ fontSize: 32, fontWeight: 500, m: 0, mb: 1.5 }}>
        {t("HowItWorksSection.heading")}
      </Typography>
      <Typography sx={{ color: "text.secondary", fontSize: 17, mb: 4, maxWidth: "60ch" }}>
        {t("HowItWorksSection.body")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", md: "repeat(6, 1fr)" },
          gap: 3,
        }}
      >
        {STAGES.map((stage) => (
          <Stack key={stage.num} spacing={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: 2,
                borderColor: stage.num === 2 || stage.num === 3 ? "secondary.main" : "text.primary",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                fontSize: 13,
                fontWeight: 500,
                color: stage.num === 2 || stage.num === 3 ? "secondary.main" : "text.primary",
              }}
            >
              {stage.num}
            </Box>
            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 500, m: 0 }}>
              {t(stage.titleKey)}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
              {t(stage.bodyKey)}
            </Typography>
          </Stack>
        ))}
      </Box>

      <Typography
        sx={{
          textAlign: "center",
          mt: 5,
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontSize: 14,
          color: "text.secondary",
        }}
      >
        <Trans
          i18nKey="HowItWorksSection.forgeLine"
          t={t}
          components={[<Box component="strong" key="s0" sx={{ color: "secondary.dark", fontWeight: 500 }} />]}
        />
      </Typography>
    </Box>
  );
}
